import InvoiceUpload from '@/containers/expense/InvoiceUpload/InvoiceUpload';
import { useAuth } from '@/providers/auth/AuthProvider';
import { LoadingOverlay } from '@mantine/core';
import { lazy } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { NothingFoundBackground } from '../components/NothingFoundBackground/NothingFoundBackground';
import DevRoute from './DevRoute';
import ProtectedRoute from './ProtectedRoute';

// Lazy load components for code splitting
/**
 * General routes
 */
const HomePage = lazy(() => import('@/pages/Home.page'));
const WelcomePage = lazy(() => import('@/pages/Welcome.page'));
const UserManagementPage = lazy(() => import('@/pages/UserManagement.page'));

/**
 * Expense routes
 */
// const InvoiceUpload = lazy(() => import('@/containers/expense/InvoiceUpload/InvoiceUpload'));
const ClaimTable = lazy(() => import('@/containers/expense/ClaimTable/ClaimTable'));
const ClaimApproval = lazy(() => import('@/containers/expense/ClaimApproval/ClaimApproval'));
const ClaimExport = lazy(() => import('@/containers/expense/ClaimExport/ClaimExport'));

/**
 * Product routes
 */
const ProductAssistant = lazy(() => import('@/containers/product/ProductAssistant/ProductAssistant'));
const ProductOrders = lazy(() => import('@/containers/product/ProductOrders/ProductOrders'));

/**
 * Organization routes
 */
const UsersPage = lazy(() => import('@/containers/organization/Users/Users'));
const GroupsPage = lazy(() => import('@/containers/organization/Groups/Groups'));
const GroupDetailsPage = lazy(() => import('@/containers/organization/Groups/GroupDetails/GroupDetails'));
const SettingsPage = lazy(() => import('@/containers/organization/Settings/Settings'));
const B1SettingMetadataUsersPage = lazy(() => import('@/containers/organization/Settings/b1/B1SettingMetadataUsers/B1SettingMetadataUsers'));

/**
 * User routes
 */
const AccountPage = lazy(() => import('@/containers/user/Account'));

/**
 * Playground routes
 */
const AnalyticsPlayground = lazy(() => import('@/containers/playground/AnalyticsPlayground/AnalyticsPlayground'));
const CustomDataTable = lazy(() => import('@/containers/playground/CustomDataTable/CustomDataTable'));
const PlaygroundInvoiceUpload = lazy(() => import('@/containers/playground/InvoiceUpload/InvoiceUpload'));

const router = createBrowserRouter([
    {
        path: '/welcome',
        element: <WelcomePage />,
    },
    {
        path: '/usermgmt',
        element: <UserManagementPage />,
    },
    {
        path: '/',
        element: <ProtectedRoute />,
        children: [
            {
                path: '',
                element: <HomePage />,
                children: [
                    {
                        path: '*',
                        element: <NothingFoundBackground />,
                    },
                    {
                        path: '',
                        element: <InvoiceUpload />,
                    },
                    {
                        path: 'expense/upload',
                        element: <InvoiceUpload />,
                    },
                    {
                        path: 'expense/claim',
                        element: <ClaimTable />,
                    },
                    {
                        path: 'expense/approval',
                        element: <ClaimApproval />,
                    },
                    {
                        path: 'expense/export',
                        element: <ClaimExport />,
                    },
                    {
                        path: 'product/assistant',
                        element: <ProductAssistant />,
                    },
                    {
                        path: 'product/orders',
                        element: <ProductOrders />,
                    },
                    {
                        path: 'organization/users',
                        element: <UsersPage />,
                    },
                    {
                        path: 'organization/groups',
                        element: <GroupsPage />,
                    },
                    {
                        path: 'organization/groups/:gid',
                        element: <GroupDetailsPage />,
                    },
                    {
                        path: 'organization/settings',
                        element: <SettingsPage />,
                    },
                    {
                        path: 'organization/settings/b1/metadata/users',
                        element: <B1SettingMetadataUsersPage />,
                    },
                    {
                        path: 'account',
                        element: <AccountPage />,
                    },
                    {
                        path: 'playground/*',
                        element: <DevRoute />,
                        children: [
                            {
                                path: '*',
                                element: <NothingFoundBackground />,
                            },
                            {
                                path: 'invoice',
                                element: <PlaygroundInvoiceUpload />,
                            },
                            {
                                path: 'datatable',
                                element: <CustomDataTable />,
                            },
                            {
                                path: 'analytics',
                                element: <AnalyticsPlayground />,
                            },
                        ],
                    },
                ],
            },
        ],
    },
]);

export function Router() {
    const { isAuthenticated } = useAuth();

    if (isAuthenticated === null) {
        return <LoadingOverlay visible zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} />;
    }

    return <RouterProvider router={router} />;
}
